import { DuplicateConflictError } from '../api/client'
import type { CustomerCreateRequest } from '../api/customers'

/**
 * Esito della creazione di un cliente dai dati della pratica, tenendo conto del controllo duplicati del server
 * (409 con i candidati: clienti, ma anche persone/aziende/contatti delle vecchie tabelle, per CF, P.IVA o email).
 * - `linked`: esiste già un solo cliente con quei dati -> si collega quello, senza crearne un altro.
 * - `choose`: più clienti corrispondono -> deve scegliere l'operatore.
 * - `created`: creato; `forced` se il server segnalava solo un doppione che non è un cliente (persona/azienda/contatto).
 */
export type CustomerLinkOutcome =
  | { kind: 'linked'; id: number }
  | { kind: 'choose'; ids: number[] }
  | { kind: 'created'; id: number; forced: boolean }

export async function createOrLinkCustomer(
  create: (data: CustomerCreateRequest, idempotencyKey: string) => Promise<{ id: number }>,
  data: CustomerCreateRequest,
  idempotencyKey: string,
): Promise<CustomerLinkOutcome> {
  try {
    return { kind: 'created', id: (await create(data, idempotencyKey)).id, forced: false }
  } catch (err) {
    if (!(err instanceof DuplicateConflictError)) throw err
    const customers = [...new Set(err.candidates.filter(c => c.entityType === 'Customer').map(c => c.id))]
    if (customers.length === 1) return { kind: 'linked', id: customers[0]! }
    if (customers.length > 1) return { kind: 'choose', ids: customers }
    // Solo doppioni fuori dall'anagrafica clienti (persona, azienda o contatto): il cliente va creato comunque.
    return { kind: 'created', id: (await create({ ...data, forceCreate: true }, `${idempotencyKey}-force`)).id, forced: true }
  }
}
